import type { WebEditorElementDefinition as LandingEditorElementDefinition, WebEditorFieldKind as LandingEditorFieldKind } from './element-definition.js';
type LandingLocale = string;
type StaticPageAudience = string;
type StaticPageKey = string;
type EditorPageKey = string;


const rootSectionDefinitionFor = (
  definition: LandingEditorElementDefinition | undefined,
  definitionById: Readonly<Record<string, LandingEditorElementDefinition>>,
) => {
  let current = definition;
  while (current?.parentId && current.parentId !== 'page.root') current = definitionById[current.parentId];
  return current?.parentId === 'page.root' ? current : undefined;
};

export type LocalizedFieldKind = Exclude<LandingEditorFieldKind, 'section' | 'container'>;

export interface LocalizationEditorContext {
  pageKey: EditorPageKey;
  audience: StaticPageAudience;
  locale: LandingLocale;
  elementId: string;
}

export interface LocalizationFieldChange {
  elementId: string;
  path: string;
  label: string;
  kind: LocalizedFieldKind;
  source: 'landing' | 'static';
  staticPageKey?: StaticPageKey;
  originalValue: string;
  currentValue: string;
}

export interface DirtyLocalizationScope {
  scopeId: string;
  scopeKind: 'section' | 'repeat' | 'page-area';
  scopeLabel: string;
  originLocale: LandingLocale;
  pageKey: EditorPageKey;
  audience: StaticPageAudience;
  originElementId: string;
  changedFields: Map<string, LocalizationFieldChange>;
  isNewScope: boolean;
}

export interface PendingLocalizationScope extends DirtyLocalizationScope {
  returnContext: LocalizationEditorContext;
}

export interface LocalizationStep {
  id: string;
  locale: LandingLocale;
  field: LocalizationFieldChange;
  status: 'pending' | 'confirmed' | 'skipped' | 'error';
}

export interface LocalizationRound {
  scope: PendingLocalizationScope;
  steps: LocalizationStep[];
  activeIndex: number;
  phase: 'preparing' | 'editing' | 'saving' | 'save-error' | 'complete';
  candidate: string;
  candidateKind: 'source' | 'manual' | 'current' | 'already-current';
  previousTargetValue: string;
  mediaOptions?: Array<{ value: string; label: string }>;
  error?: string;
}

const localizedKinds = new Set<LandingEditorFieldKind>([
  'text',
  'url',
  'icon',
  'profession',
  'freshCounterSubtype',
  'image',
  'video',
  'track',
]);

export const isLocalizedFieldDefinition = (
  definition: LandingEditorElementDefinition | undefined,
): definition is LandingEditorElementDefinition & { path: string; kind: LocalizedFieldKind } =>
  Boolean(definition?.path && localizedKinds.has(definition.kind));

const normalizedScopeLabel = (label: string) => label.replace(/\s+Container$/u, '').trim() || 'Seitenbereich';

export const localizationScopeForDefinition = (
  definition: LandingEditorElementDefinition | undefined,
  definitionById: Readonly<Record<string, LandingEditorElementDefinition>>,
  context: LocalizationEditorContext,
) => {
  if (!definition || definition.id === 'page.root') return null;
  const root = rootSectionDefinitionFor(definition, definitionById);
  const rootId = root?.id ?? definition.id.split('.')[0] ?? 'page-area';
  return {
    scopeId: `${context.pageKey}:${context.audience}:${rootId}`,
    scopeKind: root?.kind === 'section' ? ('section' as const) : ('page-area' as const),
    scopeLabel: normalizedScopeLabel(root?.label ?? definition.label),
    originElementId: root?.id ?? definition.id,
  };
};

export const recordLocalizationFieldChange = (
  scope: DirtyLocalizationScope | null,
  definition: LandingEditorElementDefinition,
  definitionById: Readonly<Record<string, LandingEditorElementDefinition>>,
  context: LocalizationEditorContext,
  originalValue: string,
  currentValue: string,
) => {
  if (!isLocalizedFieldDefinition(definition)) return scope;
  const location = localizationScopeForDefinition(definition, definitionById, context);
  if (!location) return scope;
  const belongsToRepeatScope = scope?.scopeKind === 'repeat' &&
    (definition.id === scope.originElementId || definition.id.startsWith(`${scope.originElementId}.`));
  const nextScope: DirtyLocalizationScope = scope?.scopeId === location.scopeId || belongsToRepeatScope
    ? { ...scope, changedFields: new Map(scope.changedFields) }
    : {
        ...location,
        originLocale: context.locale,
        pageKey: context.pageKey,
        audience: context.audience,
        changedFields: new Map<string, LocalizationFieldChange>(),
        isNewScope: false,
      };
  const previous = nextScope.changedFields.get(definition.id);
  const firstValue = previous?.originalValue ?? originalValue;
  if (currentValue === firstValue) {
    nextScope.changedFields.delete(definition.id);
    return nextScope.changedFields.size ? nextScope : null;
  }
  nextScope.changedFields.set(definition.id, {
    elementId: definition.id,
    path: definition.path,
    label: definition.label,
    kind: definition.kind,
    source: definition.source === 'static' ? 'static' : 'landing',
    ...(definition.staticPageKey ? { staticPageKey: definition.staticPageKey } : {}),
    originalValue: firstValue,
    currentValue,
  });
  return nextScope;
};

export const createNewLocalizationScope = (
  rootDefinition: LandingEditorElementDefinition,
  definitions: LandingEditorElementDefinition[],
  definitionById: Readonly<Record<string, LandingEditorElementDefinition>>,
  context: LocalizationEditorContext,
  valueForDefinition: (definition: LandingEditorElementDefinition & { path: string }) => string,
  repeatRootId?: string,
) => {
  const location = localizationScopeForDefinition(rootDefinition, definitionById, context);
  if (!location) return null;
  const prefix = repeatRootId ? `${repeatRootId}.` : '';
  const changes = definitions
    .filter(isLocalizedFieldDefinition)
    .filter(definition => {
      if (repeatRootId) return definition.id === repeatRootId || definition.id.startsWith(prefix);
      return localizationScopeForDefinition(definition, definitionById, context)?.scopeId === location.scopeId;
    })
    .map(definition => [definition.id, {
      elementId: definition.id,
      path: definition.path,
      label: definition.label,
      kind: definition.kind,
      source: definition.source === 'static' ? ('static' as const) : ('landing' as const),
      ...(definition.staticPageKey ? { staticPageKey: definition.staticPageKey } : {}),
      originalValue: '',
      currentValue: valueForDefinition(definition),
    }] as const);
  return {
    ...location,
    scopeId: repeatRootId ? `${location.scopeId}:repeat:${repeatRootId}` : location.scopeId,
    scopeKind: repeatRootId ? ('repeat' as const) : location.scopeKind,
    scopeLabel: repeatRootId ? normalizedScopeLabel(rootDefinition.label) : location.scopeLabel,
    originElementId: repeatRootId ?? location.originElementId,
    originLocale: context.locale,
    pageKey: context.pageKey,
    audience: context.audience,
    changedFields: new Map<string, LocalizationFieldChange>(changes),
    isNewScope: true,
  } satisfies DirtyLocalizationScope;
};

export const isLocalizationScopeExit = (
  scope: DirtyLocalizationScope | null,
  nextDefinition: LandingEditorElementDefinition | undefined,
  nextDefinitionById: Readonly<Record<string, LandingEditorElementDefinition>>,
  nextContext: LocalizationEditorContext,
) => {
  if (!scope) return false;
  if (
    scope.pageKey !== nextContext.pageKey ||
    scope.audience !== nextContext.audience ||
    scope.originLocale !== nextContext.locale ||
    nextContext.elementId === 'page.root'
  ) return true;
  if (scope.scopeKind === 'repeat') {
    return !(nextContext.elementId === scope.originElementId || nextContext.elementId.startsWith(`${scope.originElementId}.`));
  }
  return localizationScopeForDefinition(nextDefinition, nextDefinitionById, nextContext)?.scopeId !== scope.scopeId;
};

export const mergePendingLocalizationScope = (
  queue: PendingLocalizationScope[],
  pending: PendingLocalizationScope,
) => {
  const index = queue.findIndex(entry => entry.scopeId === pending.scopeId && entry.originLocale === pending.originLocale);
  if (index < 0) return [...queue, pending];
  const next = [...queue];
  const existing = next[index]!;
  next[index] = {
    ...existing,
    ...pending,
    changedFields: new Map([...existing.changedFields, ...pending.changedFields]),
    isNewScope: existing.isNewScope || pending.isNewScope,
  };
  return next;
};

export const createLocalizationSteps = (
  scope: PendingLocalizationScope,
  localeOrder: readonly LandingLocale[],
) => localeOrder
  .filter(locale => locale !== scope.originLocale)
  .flatMap(locale => [...scope.changedFields.values()].map(field => ({
    id: `${locale}:${field.elementId}`,
    locale,
    field,
    status: 'pending' as const,
  })));

export const updateLocalizationStep = (
  round: LocalizationRound,
  status: 'confirmed' | 'skipped' | 'error',
) => ({
  ...round,
  steps: round.steps.map((step, index) => index === round.activeIndex ? { ...step, status } : step),
});

/**
 * Activating another preview can synchronously rerender the shell and unmount an open inline
 * editor. Selection therefore gets the first chance to commit and flush its localization scope.
 */
export const selectPreviewElementBeforeProfileActivation = (
  elementId: string,
  selectElement: ((elementId: string) => void) | undefined,
  activateProfile: () => void,
) => {
  selectElement?.(elementId);
  activateProfile();
};

/**
 * Session-only controller. It deliberately owns no project data: candidates and navigation state
 * may disappear on reload, while every confirmed project mutation remains in the normal Draft.
 */
export class EditorLocalizationFlowController {
  dirtyScope: DirtyLocalizationScope | null = null;
  queue: PendingLocalizationScope[] = [];
  round: LocalizationRound | null = null;
  private revision = 0;
  private notify: (revision: number) => void = () => undefined;

  connect(notify: (revision: number) => void) {
    this.notify = notify;
  }

  private changed() {
    this.revision += 1;
    this.notify(this.revision);
  }

  record(
    definition: LandingEditorElementDefinition,
    definitionById: Readonly<Record<string, LandingEditorElementDefinition>>,
    context: LocalizationEditorContext,
    originalValue: string,
    currentValue: string,
  ) {
    this.dirtyScope = recordLocalizationFieldChange(
      this.dirtyScope,
      definition,
      definitionById,
      context,
      originalValue,
      currentValue,
    );
    this.changed();
  }

  /**
   * A committed WYSIWYG text edit has already left its inline editing surface. Queue the prompt in
   * the same state transition so a Lit rerender cannot strand the dirty scope between record and
   * leave. Inspector fields continue to use `record` and are grouped until their section is left.
   */
  recordWysiwygTextExit(
    definition: LandingEditorElementDefinition,
    definitionById: Readonly<Record<string, LandingEditorElementDefinition>>,
    context: LocalizationEditorContext,
    originalValue: string,
    currentValue: string,
    returnContext: LocalizationEditorContext,
  ) {
    const dirtyScope = recordLocalizationFieldChange(
      this.dirtyScope,
      definition,
      definitionById,
      context,
      originalValue,
      currentValue,
    );
    if (!dirtyScope?.changedFields.size) {
      this.dirtyScope = null;
      this.changed();
      return;
    }
    this.queue = mergePendingLocalizationScope(this.queue, { ...dirtyScope, returnContext });
    this.dirtyScope = null;
    this.changed();
  }

  markNew(scope: DirtyLocalizationScope | null) {
    if (!scope) return;
    this.dirtyScope = scope;
    this.changed();
  }

  leave(returnContext: LocalizationEditorContext) {
    if (!this.dirtyScope?.changedFields.size) {
      this.dirtyScope = null;
      return;
    }
    this.queue = mergePendingLocalizationScope(this.queue, { ...this.dirtyScope, returnContext });
    this.dirtyScope = null;
    this.changed();
  }

  dismissPrompt() {
    if (!this.queue.length) return;
    this.queue = this.queue.slice(1);
    this.changed();
  }

  dismissField(scopeId: string, elementId: string) {
    this.queue = this.queue
      .map(scope => {
        if (!scope.changedFields.has(elementId) || (scope.scopeId !== scopeId && scope.scopeKind !== 'repeat')) return scope;
        const changedFields = new Map(scope.changedFields);
        changedFields.delete(elementId);
        return { ...scope, changedFields };
      })
      .filter(scope => scope.changedFields.size > 0);
    if (this.dirtyScope?.changedFields.has(elementId) && (this.dirtyScope.scopeId === scopeId || this.dirtyScope.scopeKind === 'repeat')) {
      const changedFields = new Map(this.dirtyScope.changedFields);
      changedFields.delete(elementId);
      this.dirtyScope = changedFields.size ? { ...this.dirtyScope, changedFields } : null;
    }
    this.changed();
  }

  start(localeOrder: readonly LandingLocale[]) {
    const scope = this.queue[0];
    if (!scope) return null;
    this.queue = this.queue.slice(1);
    const steps = createLocalizationSteps(scope, localeOrder);
    this.round = {
      scope,
      steps,
      activeIndex: 0,
      phase: 'preparing',
      candidate: '',
      candidateKind: 'source',
      previousTargetValue: '',
    };
    this.changed();
    return this.round;
  }

  prepare(candidate: string, previousTargetValue: string, candidateKind: LocalizationRound['candidateKind']) {
    if (!this.round) return;
    this.round = { ...this.round, phase: 'editing', candidate, previousTargetValue, candidateKind, error: undefined };
    this.changed();
  }

  setMediaOptions(mediaOptions: Array<{ value: string; label: string }>) {
    if (!this.round) return;
    this.round = { ...this.round, mediaOptions };
    this.changed();
  }

  setCandidate(candidate: string, candidateKind: LocalizationRound['candidateKind'] = 'manual') {
    if (!this.round) return;
    this.round = { ...this.round, candidate, candidateKind };
    this.changed();
  }

  setError(error: string) {
    if (!this.round) return;
    this.round = { ...this.round, phase: 'editing', error };
    this.changed();
  }

  setPhase(phase: LocalizationRound['phase']) {
    if (!this.round) return;
    this.round = { ...this.round, phase };
    this.changed();
  }

  settle(status: 'confirmed' | 'skipped') {
    if (!this.round) return null;
    const settled = updateLocalizationStep(this.round, status);
    const nextIndex = settled.steps.findIndex((step, index) => index > settled.activeIndex && step.status === 'pending');
    this.round = nextIndex < 0
      ? { ...settled, phase: 'saving' }
      : {
          ...settled,
          activeIndex: nextIndex,
          phase: 'preparing',
          candidate: '',
          previousTargetValue: '',
          mediaOptions: undefined,
          error: undefined,
        };
    this.changed();
    return this.round;
  }

  skipLocale() {
    if (!this.round) return null;
    const activeLocale = this.round.steps[this.round.activeIndex]?.locale;
    if (!activeLocale) return this.round;
    const steps = this.round.steps.map((step, index) =>
      index >= this.round!.activeIndex && step.locale === activeLocale && step.status === 'pending'
        ? { ...step, status: 'skipped' as const }
        : step,
    );
    const nextIndex = steps.findIndex((step, index) => index > this.round!.activeIndex && step.status === 'pending');
    this.round = nextIndex < 0
      ? { ...this.round, steps, phase: 'saving' }
      : { ...this.round, steps, activeIndex: nextIndex, phase: 'preparing', candidate: '', mediaOptions: undefined, error: undefined };
    this.changed();
    return this.round;
  }

  previous() {
    if (!this.round) return null;
    const previousIndex = this.round.steps.map(step => step.status).lastIndexOf('pending', this.round.activeIndex - 1);
    const fallback = Math.max(0, this.round.activeIndex - 1);
    this.round = {
      ...this.round,
      activeIndex: previousIndex >= 0 ? previousIndex : fallback,
      phase: 'preparing',
      candidate: '',
      mediaOptions: undefined,
      error: undefined,
    };
    this.changed();
    return this.round;
  }

  finish() {
    const finished = this.round;
    this.round = null;
    this.changed();
    return finished;
  }
}
